import { useState, useCallback, useRef, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa6';
import { FiMoreVertical, FiEye, FiClock, FiSettings, FiTrash2 } from 'react-icons/fi';
import { MdUndo } from 'react-icons/md';
import { toast } from 'react-toastify';
import PageList from '../../templates/PageList';
import DeactivateModal from './shared/DeactivateModal';
import FilterBar from './shared/FilterBar';
import type { FilterSchema, AppliedFilter, CheckItem } from './shared/FilterBar';
import VerificationConfigPageForm from './VerificationConfigPageForm';
import BatchStatusBadge from './shared/BatchStatusBadge';
import CriteriaConfigPanelEdit from './CriteriaConfigPanelEdit';
import { formatCurrency, formatCpfCnpj, formatDate, getIntensityColor } from '../../../utils/collateralFormatters';
import type { AnalysisIntensity } from '../../../types/Collateral';
import useVerificationConfigPageList from '../../../hooks/useVerificationConfigPageList';
import { collateralApi } from '../../../services/collateralApi';
import OperationLogsDrawer from './shared/OperationLogsDrawer';
import RestoreModal from './shared/RestoreModal';

// ─── Filter Schema ──────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
    { value: 'NOT_STARTED', label: 'Não iniciado' },
    { value: 'IN_PROGRESS', label: 'Em validação' },
    { value: 'PARTIALLY_VALIDATED', label: 'Parcialmente validada' },
    { value: 'DONE', label: 'Concluído' },
];

const filterSchema: FilterSchema[] = [
    { key: 'guaranteeStatus', label: 'Status documental', type: 'enum', options: STATUS_OPTIONS },
    { key: 'validationStatus', label: 'Status validação', type: 'enum', options: STATUS_OPTIONS },
    { key: 'finalStatus', label: 'Status final', type: 'enum', options: STATUS_OPTIONS },
    { key: 'intensity', label: 'Intensidade', type: 'enum', options: [
        { value: 'BAIXA', label: 'Baixa' },
        { value: 'MEDIA', label: 'Média' },
        { value: 'ALTA', label: 'Alta' },
        { value: 'CAOTICA', label: 'Caótica' },
    ]},
    { key: 'operationType', label: 'Tipo operação', type: 'enum', options: [
        { value: 'CESSAO', label: 'Cessão' },
        { value: 'CPRF', label: 'CPRF' },
    ]},
    { key: 'obligationType', label: 'Obrigação', type: 'enum', options: [
        { value: 'WITHOUT_RECOURSE', label: 'Sem regresso' },
        { value: 'WITH_RECOURSE', label: 'Com regresso' },
        { value: 'NO_OBLIGATION', label: 'Sem garantia' },
    ]},
];

// ─── Table Columns ──────────────────────────────────────────────────────────────

const columns = [
    { id: 'operationNumber', title: 'Número', field: 'operationNumber', canSort: true, width: '100px', cssClass: 'sticky-left z-20' },
    { id: 'assignor', title: 'Cedente', field: 'assignor', canSort: true, width: '250px', cssClass: 'sticky-left-1 z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]' },
    { id: 'op', title: 'Operação', field: 'operationCode', canSort: true, width: '200px' },
    { id: 'issueDate', title: 'Emissão', field: 'issueDate', canSort: true, width: '90px', cssClass: 'text-center' },
    { id: 'obligation', title: 'Obrigação', field: 'obligation', canSort: false, width: '100px', cssClass: 'text-center' },
    { id: 'totalAmount', title: 'Valor títulos', field: 'totalAmount', canSort: true, width: '100px', cssClass: 'text-right' },
    { id: 'valuePct', title: '% valor', field: 'totalAmount', canSort: false, width: '70px', cssClass: 'text-center' },
    { id: 'receivables', title: 'Recebíveis', field: 'totalReceivables', canSort: true, width: '80px', cssClass: 'text-center' },
    { id: 'intensity', title: 'Intensidade', field: 'analysisIntensity', canSort: false, width: '90px', cssClass: 'text-center' },
    { id: 'guaranteeStatus', title: 'Status documental', field: 'guaranteeStatus', canSort: false, width: '90px', cssClass: 'text-center' },
    { id: 'validationStatus', title: 'Status validação', field: 'validationStatus', canSort: false, width: '90px', cssClass: 'text-center' },
    { id: 'finalStatus', title: 'Status final', field: 'finalStatus', canSort: false, width: '90px', cssClass: 'text-center' },
    { id: 'actions', title: '', field: 'actions', canSort: false, width: '30px', cssClass: 'text-center' },
];

// ─── Page Component ─────────────────────────────────────────────────────────────

const VerificationConfigPageList = () => {
    const {
        rows, total, page, setPage, pageSize,
        isLoading, initialLoading,
        showNewForm, setShowNewForm,
        searchInput, setSearchInput, search, setSearch,
        sortField, setSortField, sortOrder, setSortOrder,
        showInactive, setShowInactive,
        refresh, handleFilterSearch, handleRowClick,
    } = useVerificationConfigPageList();

    const [editingConfig, setEditingConfig] = useState<any>(null);
    const [deactivatingConfig, setDeactivatingConfig] = useState<any>(null);
    const [isDeactivating, setIsDeactivating] = useState(false);
    const [actionMenuRow, setActionMenuRow] = useState<any>(null);
    const [logsRow, setLogsRow] = useState<any>(null);
    const [logsData, setLogsData] = useState<any[]>([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [restoringConfig, setRestoringConfig] = useState<any>(null);
    const [isRestoring, setIsRestoring] = useState(false);
    const actionMenuRef = useRef<HTMLDivElement>(null);

    const handleDeactivate = async (reason: string) => {
        if (!deactivatingConfig) return;
        setIsDeactivating(true);
        try {
            await collateralApi.deactivateVerificationConfig(deactivatingConfig.id, reason);
            toast.success('Verificação desativada com sucesso');
            setDeactivatingConfig(null);
            refresh();
        } catch {
            toast.error('Erro ao desativar verificação');
        } finally {
            setIsDeactivating(false);
        }
    };

    // Close action menu on outside click
    useEffect(() => {
        if (!actionMenuRow) return;
        const handler = (e: MouseEvent) => {
            if (actionMenuRef.current && !actionMenuRef.current.contains(e.target as Node)) {
                setActionMenuRow(null);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [actionMenuRow]);

    const openLogs = async (row: any) => {
        setLogsRow(row);
        setLogsData([]);
        setLogsLoading(true);
        try {
            const data = await collateralApi.getVerificationConfigView(row.id);
            setLogsData(data?.operationLogs || []);
        } catch {
            setLogsData([]);
        } finally {
            setLogsLoading(false);
        }
    };

    const handleRestore = async (reason: string) => {
        if (!restoringConfig) return;
        setIsRestoring(true);
        try {
            await collateralApi.restoreVerificationConfig(restoringConfig.id, reason);
            toast.success('Verificação restaurada com sucesso');
            setRestoringConfig(null);
            refresh();
        } catch {
            toast.error('Erro ao restaurar verificação');
        } finally {
            setIsRestoring(false);
        }
    };

    const onFilterSearch = useCallback((filters: AppliedFilter[]) => {
        handleFilterSearch(filters);
    }, [handleFilterSearch]);

    const renderRows = (_apiRows: any[]) => {
        if (rows.length === 0) {
            return (
                <tr>
                    <td colSpan={columns.length} className="py-8 text-center text-gray-500 dark:text-gray-400">
                        Nenhuma análise encontrada
                    </td>
                </tr>
            );
        }
        return rows.map((row: any, index: number) => (
            <tr
                key={row.id}
                className={`group hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'} ${!row.active ? '[&>td:not(:last-child)]:opacity-60' : ''}`}
                onClick={() => handleRowClick(row)}
            >
                <td className={`py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm font-medium dark:text-white sticky-left z-20 ${!row.active ? 'border-l-2 border-l-red-500' : ''}`} style={{width: '100px', minWidth: '100px'}}>
                    <div className="truncate" title={row.operationNumber}>{row.operationNumber || '—'}</div>
                </td>
                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white sticky-left-1 z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]" style={{width: '250px', minWidth: '250px'}} title={row.assignor}>
                    <div className="truncate">{row.assignor}</div>
                    <div className="text-xs text-gray-400 dark:text-gray-500">{formatCpfCnpj(row.assignorTaxId)}</div>
                    {!row.active && row.deactivationReason && (
                        <div className="text-[10px] text-red-500 dark:text-red-400 truncate mt-0.5 italic">
                            {row.deactivationReason.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim()}
                        </div>
                    )}
                </td>
                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white max-w-[200px]" title={row.operationCode}>
                    <div className="truncate">{row.operationCode}</div>
                </td>
                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center text-sm dark:text-gray-300">
                    {formatDate(row.issueDate)}
                </td>
                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center text-xs dark:text-gray-300">
                    {row.operationType && (
                        <div><span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium leading-tight bg-slate-100 text-slate-700 dark:bg-slate-600 dark:text-slate-200">{row.operationType}</span></div>
                    )}
                    {row.obligation && (
                        <div className="mt-0.5"><span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium leading-tight bg-blue-50 text-blue-700 dark:bg-blue-900 dark:text-blue-300">{row.obligation}</span></div>
                    )}
                </td>
                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-right text-sm dark:text-white">
                    <div>{formatCurrency(row.totalAmount || 0)}</div>
                    <div className="text-[10px] text-green-600">{formatCurrency(row.validatedAmount || 0)}</div>
                </td>
                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center text-xs dark:text-white">
                    <span className={`font-medium ${row.totalAmount > 0 && row.validatedAmount >= row.totalAmount ? 'text-green-600' : ''}`}>
                        {row.totalAmount > 0 ? Math.round((row.validatedAmount || 0) / row.totalAmount * 100) : 0}%
                    </span>
                </td>
                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center text-xs dark:text-white">
                    <span>{row.validatedReceivables || 0}/{row.totalReceivables || 0}</span>
                </td>
                <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                    {row.analysisIntensity && row.analysisIntensity !== '—'
                        ? <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getIntensityColor(row.analysisIntensity as AnalysisIntensity)}`}>{row.analysisIntensity}</span>
                        : <span className="text-xs text-gray-400">{row.analysisIntensity}</span>}
                </td>
                <td className="py-2 px-2 border-b border-gray-200 dark:border-slate-700 text-center">
                    <BatchStatusBadge status={row.guaranteeStatus} statusFormatted={row.guaranteeStatusFormatted || row.guaranteeStatus} className="inline-block leading-tight" />
                </td>
                <td className="py-2 px-2 border-b border-gray-200 dark:border-slate-700 text-center">
                    {(row.criteria || []).some((c: any) => c.criteriaType === 'DRAWEE_CONFIRMATION') &&
                        <BatchStatusBadge status={row.validationStatus} statusFormatted={row.validationStatusFormatted || row.validationStatus} className="inline-block leading-tight" />}
                </td>
                <td className="py-2 px-2 border-b border-gray-200 dark:border-slate-700 text-center">
                    {(row.criteria || []).some((c: any) => c.criteriaType === 'DRAWEE_CONFIRMATION') &&
                        <BatchStatusBadge status={row.finalStatus} statusFormatted={row.finalStatusFormatted || row.finalStatus} className="inline-block leading-tight" />}
                </td>
                <td className="py-2 px-1 border-b border-gray-200 dark:border-slate-700 text-center" onClick={e => e.stopPropagation()}>
                    <div className="relative" ref={actionMenuRow?.id === row.id ? actionMenuRef : undefined}>
                        <button
                            type="button"
                            aria-label="Menu de ações"
                            title="Ações"
                            onClick={() => setActionMenuRow(actionMenuRow?.id === row.id ? null : row)}
                            className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-200 dark:border-slate-600 opacity-0 group-hover:opacity-100 transition-all text-gray-500 hover:text-gray-800 hover:border-gray-400 hover:shadow-sm dark:hover:text-gray-200 dark:hover:border-slate-400 cursor-pointer"
                        >
                            <FiMoreVertical size={14} />
                        </button>
                        {actionMenuRow?.id === row.id && (
                            <div className="absolute right-0 top-full mt-1 bg-white dark:bg-slate-800 border dark:border-slate-600 rounded-lg shadow-lg z-50 py-1 min-w-[160px]">
                                <button onClick={() => { setActionMenuRow(null); handleRowClick(row); }} className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300">
                                    <FiEye size={12} /> Detalhes
                                </button>
                                <button onClick={() => { setActionMenuRow(null); openLogs(row); }} className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300">
                                    <FiClock size={12} /> Histórico
                                </button>
                                {row.active && (
                                    <>
                                        <hr className="my-1 border-gray-200 dark:border-slate-600" />
                                        <button onClick={() => { setActionMenuRow(null); setEditingConfig(row); }} className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300">
                                            <FiSettings size={12} /> Configuração
                                        </button>
                                    </>
                                )}
                                {row.active && (
                                    <>
                                        <hr className="my-1 border-gray-200 dark:border-slate-600" />
                                        <button onClick={() => { setActionMenuRow(null); setDeactivatingConfig(row); }} className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-slate-700 text-red-600 dark:text-red-400">
                                            <FiTrash2 size={12} /> Desativar
                                        </button>
                                    </>
                                )}
                                {!row.active && (
                                    <>
                                        <hr className="my-1 border-gray-200 dark:border-slate-600" />
                                        <button onClick={() => { setActionMenuRow(null); setRestoringConfig(row); }} className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-slate-700 text-green-600 dark:text-green-400">
                                            <MdUndo size={12} /> Restaurar
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </td>
            </tr>
        ));
    };

    // URL vazia desabilita o fetch interno do PageList — o hook gerencia o fetch
    const url = '';
    const fetchHeaders: { key: string; value: string }[] = [];

    return (
        <>
            <PageList
                pageTitle="Verificação de Recebíveis"
                url={url}
                fetchHeaders={fetchHeaders}
                breadcrumb={[
                    { label: 'Verificação de Recebíveis', url: '/collateral/verification-config' },
                ]}
                tableHeaders={columns}
                defaultBackendHeader={false}
                tableFieldSort={sortField}
                tableOrderSort={sortOrder}
                onTableSort={(field: string, order: 'ASC' | 'DESC') => { setSortField(field); setSortOrder(order); }}
                renderRows={renderRows}
                page={page - 1}
                pageSize={pageSize}
                onPageChange={(p: number) => setPage(p + 1)}
                onItemsPerPageChange={() => {}}
                itemsLabel="verificações"
                isLoadingData={isLoading || initialLoading}
                externalTotalElements={total}
                externalTotalPages={Math.ceil(total / pageSize)}
            >
                {{
                    filterSlot: (
                        <FilterBar
                            schema={filterSchema}
                            onSearch={onFilterSearch}
                            searchValue={searchInput}
                            onSearchChange={setSearchInput}
                            onSearchSubmit={(value?: string) => { setSearch(value ?? searchInput); setPage(1); }}
                            searchPlaceholder="Buscar operação, cedente, CNPJ, sacado..."
                            loading={isLoading}
                            storageKey="collateral:verification-config:filters"
                            checkItems={[
                                { key: 'showInactive', label: 'Mostrar desativados', checked: showInactive, onChange: setShowInactive },
                            ]}
                        />
                    ),
                    controlSlot: (
                        <button
                            onClick={() => setShowNewForm(true)}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                        >
                            <FaPlus size={12} />
                            Nova Verificação
                        </button>
                    ),
                }}
            </PageList>

            <Outlet context={{ onDataChange: refresh }} />

            {showNewForm && (
                <div className="fixed inset-0 z-50 flex justify-end">
                    <div className="absolute inset-0 bg-black/30" onClick={() => setShowNewForm(false)} />
                    <div className="relative w-full max-w-lg bg-white dark:bg-slate-800 h-full overflow-auto p-6 shadow-xl">
                        <VerificationConfigPageForm
                            onClose={() => setShowNewForm(false)}
                            onCreated={() => { setShowNewForm(false); refresh(); toast.success('Análise criada'); }}
                        />
                    </div>
                </div>
            )}

            {editingConfig && (
                <CriteriaConfigPanelEdit
                    configUuid={editingConfig.id}
                    validationConfig={Object.fromEntries(
                        (editingConfig.criteria || []).map((c: any) => {
                            const keyMap: Record<string, string> = {
                                XML: 'validation_xml', INVOICE_PDF: 'validation_invoice_pdf',
                                RECEIPT_STUB: 'validation_receipt_stub', DRAWEE_CONFIRMATION: 'validation_contact',
                                SEFAZ: 'validation_sefaz', ACCEPTED_BILL: 'validation_accepted_bill',
                                PURCHASE_CONTRACT: 'validation_purchase_contract',
                            };
                            return [keyMap[c.criteriaType] || c.criteriaType, c.timing];
                        })
                    )}
                    operationSummary={{ operationCode: editingConfig.operationCode || '', assignorName: editingConfig.assignor || '', totalAmount: editingConfig.totalAmount || 0, totalReceivables: editingConfig.totalReceivables || 0 }}
                    operationType={editingConfig.operationType || ''}
                    obligationType={editingConfig.obligationType || ''}
                    traderName={editingConfig.traderName || ''}
                    analysisCriteria={editingConfig.analysisMode === 'SAMPLE' ? 'Amostragem' : 'Completa'}
                    sampledTitles={editingConfig.sampleCount || 1}
                    hasDraweeInProgress={editingConfig.validationStatus === 'Parcialmente validada' || editingConfig.validationStatus === 'Em validação'}
                    onClose={() => setEditingConfig(null)}
                    onSaved={() => { setEditingConfig(null); refresh(); }}
                />
            )}

            <DeactivateModal
                isOpen={!!deactivatingConfig}
                operationCode={deactivatingConfig?.operationCode || ''}
                loading={isDeactivating}
                onCancel={() => setDeactivatingConfig(null)}
                onConfirm={handleDeactivate}
            />

            <OperationLogsDrawer
                isOpen={!!logsRow}
                logs={logsData}
                loading={logsLoading}
                onClose={() => { setLogsRow(null); setLogsData([]); }}
            />

            <RestoreModal
                isOpen={!!restoringConfig}
                operationCode={restoringConfig?.operationCode || ''}
                loading={isRestoring}
                onCancel={() => setRestoringConfig(null)}
                onConfirm={handleRestore}
            />
        </>
    );
};

export default VerificationConfigPageList;
